import { AppCardTitle, AppToggleButton } from '@components'
import React from 'react'
import {
	Box, Card, CardContent, Table, TableHead, TableBody, TableRow, TableCell,
	Grid
} from '@mui/material'
import { IconSeeker } from '@ui-seeker/core'
import { dsColorNew } from "@palette"
import { alpha } from "@mui/material/styles"


const DATES = ['10 Feb', '11 Feb', '12 Feb', '13 Feb']

const DEMO_ROWS = [
	{
		office: 'Madrid Airport T4',
		cells: [
			{ name: 'FleetMax', price: '€38.50', type: 'cheaper', diff: 12.3 },
			{ name: 'DrivePro', price: '€45.20', type: 'more expensive', diff: 8.1 },
			{ name: 'FleetMax', price: '€36.80', type: 'cheaper', diff: 15.7 },
			{ name: 'CarFirst', price: '€42.90', type: 'equal', diff: null },
		],
	},
	{
		office: 'Barcelona El Prat',
		cells: [
			{ name: 'DrivePro', price: '€52.10', type: 'more expensive', diff: 6.4 },
			{ name: 'FleetMax', price: '€44.30', type: 'cheaper', diff: 9.8 },
			{ name: 'AutoRent', price: '€49.70', type: 'more expensive', diff: 3.2 },
			{ name: 'FleetMax', price: '€41.50', type: 'cheaper', diff: 11.5 },
		],
	},
	{
		office: 'Malaga Costa del Sol',
		cells: [
			{ name: 'CarFirst', price: '€35.90', type: 'cheaper', diff: 18.2 },
			{ name: 'SpeedCar', price: '€48.60', type: 'more expensive', diff: 5.3 },
			{ name: 'CarFirst', price: '€37.10', type: 'cheaper', diff: 14.9 },
			{ name: 'DrivePro', price: '€46.80', type: 'equal', diff: null },
		],
	},
	{
		office: 'Valencia Center',
		cells: [
			{ name: 'AutoRent', price: '€41.20', type: 'more expensive', diff: 4.7 },
			{ name: 'CarFirst', price: '€39.80', type: 'cheaper', diff: 7.6 },
			{ name: 'SpeedCar', price: '€50.30', type: 'more expensive', diff: 10.1 },
			{ name: 'AutoRent', price: '€43.60', type: 'cheaper', diff: 5.9 },
		],
	},
	{
		office: 'Palma de Mallorca',
		cells: [
			{ name: 'SpeedCar', price: '€47.80', type: 'equal', diff: null },
			{ name: 'FleetMax', price: '€36.50', type: 'cheaper', diff: 20.1 },
			{ name: 'DrivePro', price: '€53.20', type: 'more expensive', diff: 7.8 },
			{ name: 'CarFirst', price: '€39.90', type: 'cheaper', diff: 13.4 },
		],
	},
]

// Same comparison colours and arrows as the page's price column (Table/@columns/price.jsx)
const COMPARISON_COLORS = {
	'cheaper': dsColorNew.dangerRed,
	'more expensive': dsColorNew.appBlue,
}

const COLOR_MAP = {
	'cheaper': alpha(COMPARISON_COLORS['cheaper'], 0.45),
	'more expensive': alpha(COMPARISON_COLORS['more expensive'], 0.45),
	'equal': dsColorNew.grayBorder,
}

const BG_MAP = {
	'cheaper': alpha(COMPARISON_COLORS['cheaper'], 0.08),
	'more expensive': alpha(COMPARISON_COLORS['more expensive'], 0.08),
	'equal': dsColorNew.white,
}

const ICON_MAP = {
	'cheaper': { icon: 'arrow-down', color: dsColorNew.dangerRed },
	'more expensive': { icon: 'arrow-up', color: dsColorNew.successText },
	'equal': { icon: 'equals', color: dsColorNew.textMuted },
}


// The leader's price, from the main price and the difference (the main price is below it when cheaper)
function leaderPrice({ price, type, diff }) {
	const main = parseFloat(price.replace('€', ''))
	const leader = type === 'cheaper' ? main * (1 + diff / 100) : type === 'more expensive' ? main * (1 - diff / 100) : main
	return `€${leader.toFixed(2)}`
}

// Same cell as the page's price column: the main price, then the leader's price and the difference under it
function PriceCell({ data }) {
	return <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
		<Box sx={{
			display: 'flex', alignItems: 'center', justifyContent: 'space-around',
			borderRadius: '8px', border: '1px solid', width: 130, minHeight: 40,
			borderColor: COLOR_MAP[data.type], backgroundColor: BG_MAP[data.type],
		}}>
			<Box sx={{ py: 0.5, px: 1, width: '100%', textAlign: 'center' }}>
				<Box sx={{ fontSize: 15, fontWeight: 700, color: dsColorNew.textTitle }}>{data.price}</Box>
				<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
					<Box sx={{ fontSize: 11, color: dsColorNew.textMuted }}>{leaderPrice(data)}</Box>
					{!!data.diff && <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, color: ICON_MAP[data.type]?.color }}>
						<IconSeeker icon={ICON_MAP[data.type]?.icon} sx={{ fontSize: 10 }} />
						<Box sx={{ fontSize: 11 }}>{data.diff}%</Box>
					</Box>}
				</Box>
			</Box>
		</Box>
	</Box>
}


export default function LeadingDemoData() {
	return <Grid container spacing={2}>
		{/* Table */}
		<Grid item xs={12}>
			<Card>
				<CardContent sx={{ p: 2 }}>
					<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
						<AppCardTitle icon="trophy" title="Leading Price Table"/>
						<AppToggleButton value="min" onChange={() => {}} buttonProps={{ sx: { px: 2.5, py: 1 } }} tabs={[{ value: 'min', label: 'Min. Price' }, { value: 'avg', label: 'Avg. Price' }, { value: 'max', label: 'Max. Price' }]}/>
					</Box>
					<Box sx={{ overflowX: 'auto' }}>
						<Table size="small">
							<TableHead>
								<TableRow sx={{ '& th': { textAlign: 'center' } }}>
									<TableCell sx={{ position: 'sticky', left: 0, zIndex: 2, minWidth: 160, textAlign: 'left !important' }}>Office</TableCell>
									{DATES.map(d => <TableCell key={d}>{d}</TableCell>)}
								</TableRow>
							</TableHead>
							<TableBody>
								{DEMO_ROWS.map((row, ri) => (
									<TableRow key={ri} sx={{ backgroundColor: ri % 2 === 0 ? dsColorNew.white : dsColorNew.tableRowAlt }}>
										<TableCell sx={{
											position: 'sticky', left: 0, zIndex: 1, fontWeight: 600,
											fontSize: 13, bgcolor: 'inherit', color: dsColorNew.textTitle, minWidth: 160,
											boxShadow: `6px 0 8px -6px ${alpha(dsColorNew.textTitle, 0.18)}`,
										}}>
											{row.office}
										</TableCell>
										{row.cells.map((cell, ci) => (
											<TableCell key={ci} sx={{ textAlign: 'center', p: 1 }}>
												<PriceCell data={cell} />
											</TableCell>
										))}
									</TableRow>
								))}
							</TableBody>
						</Table>
					</Box>
				</CardContent>
			</Card>
		</Grid>
	</Grid>
}
